import { useAsyncScript } from "library/hooks/useAsyncScript";
import { getQuickstreamOptions } from "library/hooks/useQuickStream/getQuickStreamOptions";
import { TokenError } from "library/hooks/useQuickStream/TokenError";
import { useCallback, useEffect, useRef, useState } from "react";

interface QuickStreamOptions {
  supplierBusinessCode: string;
  region: string;
  inputStyle?: QuickstreamAPI.StyleMap;
}

export interface SingleUseToken {
  maskedCardNumber: string;
  cardScheme: string;
  lastFour: string;
  token: string;
}

type QuickStreamRefCallback = (node: HTMLDivElement) => void;

interface TrustedFrame {
  teardown: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  submitForm: (callback: any) => void;
}

type Status = "LOADING" | "READY";

export const useQuickStream = ({
  supplierBusinessCode,
  region,
  inputStyle,
}: QuickStreamOptions) => {
  const scriptStatus = useAsyncScript(
    process.env.REACT_APP_QUICK_STREAM_URL as string,
  );

  const [status, setStatus] = useState<Status>("LOADING");
  const trustedFrameInstance = useRef<TrustedFrame | null>(null);
  const styleOverrides = useRef<QuickstreamAPI.StyleMap | undefined>(
    inputStyle,
  );
  const isQuickStreamNodeMounted = useRef(false);
  const isQuickStreamInitialized = useRef(false);

  const initializeQuickstream = useCallback(() => {
    if (
      scriptStatus !== "ready" ||
      !isQuickStreamNodeMounted.current ||
      isQuickStreamInitialized.current
    ) {
      return;
    }

    isQuickStreamInitialized.current = true;

    window.QuickstreamAPI.init({
      publishableApiKey:
        region === "AU"
          ? process.env.REACT_APP_QUICK_STREAM_KEY_AU
          : process.env.REACT_APP_QUICK_STREAM_KEY_NZ,
    });

    const options = getQuickstreamOptions({
      supplierBusinessCode,
      inputStyleOverrides: styleOverrides.current,
    });

    window.QuickstreamAPI.creditCards.createTrustedFrame(
      options,
      (errors, data) => {
        if (data) {
          trustedFrameInstance.current = data.trustedFrame;
          setStatus("READY");
        }
      },
    );
  }, [scriptStatus, supplierBusinessCode, region]);

  const quickStreamRef = useCallback<QuickStreamRefCallback>(
    (node) => {
      if (!node) {
        return;
      }

      node.setAttribute("data-quickstream-api", "creditCardContainer");
      node.style.position = "relative";
      node.style.height = "450px";
      isQuickStreamNodeMounted.current = true;
      isQuickStreamInitialized.current = false;

      initializeQuickstream();
    },
    [initializeQuickstream],
  );

  useEffect(() => {
    initializeQuickstream();
  }, [initializeQuickstream]);

  const getToken = useCallback(
    () =>
      new Promise<SingleUseToken>((resolve, reject) => {
        if (!trustedFrameInstance.current) {
          return;
        }
        trustedFrameInstance.current.submitForm((errors, data) => {
          if (data?.token) {
            const singleUseToken: SingleUseToken = {
              maskedCardNumber: data.singleUseToken.creditCard.cardNumber,
              lastFour:
                data.singleUseToken.creditCard.maskedCardNumber4Digits.substr(
                  -4,
                ),
              cardScheme: data.singleUseToken.creditCard.cardScheme,
              token: data.token,
            };
            resolve(singleUseToken);
          } else if (errors) {
            reject(new TokenError(errors));
          }
        });
      }),
    [],
  );

  return {
    status,
    getToken,
    quickStreamRef,
  };
};
